import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DtoCubeDataResponse,
  DtoTestSetBenchmark,
} from "../../../../types/dto-types";
import { Category } from "../types";
import { CubeSideName } from "../components/Сube/types";

export interface Benchmark {
  id: string;
  title: string;
  value: number;
  children?: Benchmark[];
  parentId?: string;
}

export interface UseBenchmarksReturn {
  benchmarks: Benchmark[];
}

export interface UseBenchmarksArgs {
  currentTestId: string;
  rawData: DtoCubeDataResponse | undefined;
  category: Category | undefined;
  cubeSide: CubeSideName | undefined;
}

export function useBenchmarks({
  rawData,
  category,
  cubeSide,
  currentTestId,
}: UseBenchmarksArgs): UseBenchmarksReturn {
  const { t } = useTranslation();

  const defaultBenchmarkTitle = t(
    "frontend.futurebilityCockpit.defaultBenchmark",
    "Default"
  );

  // Memoize filtered test sets separately for better performance
  const filteredTestSets = useMemo(() => {
    if (!rawData?.testSets) return [];

    return rawData.testSets.filter(ts => {
      if (ts.testId !== currentTestId || !ts.info?.defaultBenchmark) {
        return false;
      }

      if (!ts.benchmarks?.length) return false;

      if (category && ts.category !== category) return false;
      if (cubeSide && ts.info?.cubeSide !== cubeSide) return false;

      return true;
    });
  }, [rawData?.testSets, currentTestId, category, cubeSide]);

  return useMemo(() => {
    if (filteredTestSets.length === 0) {
      return {
        benchmarks: [
          {
            id: "default",
            title: defaultBenchmarkTitle,
            value: 0,
          },
        ],
      };
    }

    const templateTestSet = filteredTestSets[0];
    const testSetCount = filteredTestSets.length;

    // Calculate average default benchmark value
    const avgDefaultBenchmark =
      filteredTestSets.reduce(
        (sum, ts) => sum + (ts.info.defaultBenchmark ?? 0),
        0
      ) / testSetCount;

    // Average benchmarks from all filtered test sets
    const averagedBenchmarks = templateTestSet.benchmarks.map(
      (templateBenchmark, index) => {
        const { label, values } = templateBenchmark;

        if (values <= 0) {
          return { id: label, title: label, value: 0 };
        }

        // Pre-collect all benchmarks at this index for faster access
        const benchmarksAtIndex = filteredTestSets.map(
          ts => ts.benchmarks[index]
        );

        const averagedChildren: Benchmark[] = [];

        for (let n = 1; n <= values; n++) {
          const titleKey = `a${n}` as keyof DtoTestSetBenchmark;
          const valueKey = `b${n}` as keyof DtoTestSetBenchmark;

          const title = String(templateBenchmark[titleKey] ?? `Benchmark ${n}`);

          // Calculate average value across all filtered test sets
          const avgValue =
            benchmarksAtIndex.reduce((sum, benchmark) => {
              const value = benchmark?.[valueKey];
              return sum + (typeof value === "number" ? value : 0);
            }, 0) / testSetCount;

          averagedChildren.push({
            id: title,
            parentId: label,
            title,
            value: avgValue,
          });
        }

        return {
          id: label,
          title: label,
          value: 0,
          children: averagedChildren,
        };
      }
    );

    return {
      benchmarks: [
        {
          id: "default",
          title: defaultBenchmarkTitle,
          value: avgDefaultBenchmark,
        },
        ...averagedBenchmarks,
      ],
    };
  }, [filteredTestSets, defaultBenchmarkTitle]);
}
